import { useEffect } from 'react';
import { useSelector } from 'react-redux';

import { selectBrandTheme } from 'store/theme/selectors';

/**
 * Applies the building's brand colour to the `@hqo/ui-atoms` CSS-variable layer.
 *
 * `useBuildingTheme` brands the styled-components / RCL theme (via `AppThemeProvider`), but
 * `@hqo/ui-atoms` components don't read that theme — they read CSS variables (`--primary`, …) from
 * `@hqo/tailwind-theme`, which default to `--system-blue`. When visitor-registration runs standalone
 * in a host miniapp (e.g. a mobile webview) there is no host ancestor setting those variables, so
 * ui-atoms controls render system blue instead of the building brand.
 *
 * Only `--primary` and `--ring` are remapped — the brand-safe token set. `--secondary` is a
 * contrast-paired token and `--accent` / `--muted` are neutral surface tokens (skeletons, hovers,
 * muted fills), so pointing them at the brand mis-tints those surfaces; they are left at their
 * tailwind-theme defaults.
 */
export const useBrandCssVariables = (): void => {
  const brandTheme = useSelector(selectBrandTheme);
  const primaryColor = brandTheme?.primary_color;

  useEffect(() => {
    if (!primaryColor) {
      return undefined;
    }

    const root = document.documentElement;
    root.style.setProperty('--primary', primaryColor);
    root.style.setProperty('--ring', primaryColor);

    return () => {
      root.style.removeProperty('--primary');
      root.style.removeProperty('--ring');
    };
  }, [primaryColor]);
};
